import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFossasSepticas } from '../context/FossasSepticasProvider';
import { MapContainer, TileLayer, Marker, Popup, LayersControl, LayerGroup } from 'react-leaflet';
import { createCustomIcon, calculateCenter } from '../utils/mapHelpers';
import { formatStatus, formatCoordinates } from '../utils/formatters';
import 'leaflet/dist/leaflet.css';

/**
 * Componente que exibe o mapa de propriedades rurais e fossas sépticas
 */
const MapaPropriedadesRurais = () => {
  const { 
    propriedades, 
    residencias,
    fossas,
    carregarPropriedades, 
    carregarResidencias,
    carregarFossas,
    loading, 
    filtros, 
    setFiltros,
    localidades,
    agentes,
    carregarLocalidades,
    carregarAgentes
  } = useFossasSepticas();

  const [searchParams] = useSearchParams();
  // Coordenadas de Conceição do Castelo - ES (mesmo padrão dos outros módulos)
  const centroConceicaoCastelo = [-20.359292, -41.247282];
  const [mapCenter, setMapCenter] = useState(centroConceicaoCastelo);
  const [zoom, setZoom] = useState(13);
  const [showResidencias, setShowResidencias] = useState(true);
  const [showFossas, setShowFossas] = useState(true);

  // Carregar dados ao montar o componente
  useEffect(() => {
    const fetchData = async () => {
      await carregarLocalidades();
      await carregarAgentes();
      await carregarPropriedades();
      await carregarResidencias();
      await carregarFossas();
    };
    
    fetchData();
  }, [carregarPropriedades, carregarResidencias, carregarFossas, carregarLocalidades, carregarAgentes]);

  // Verificar se há um ID de propriedade na URL para centralizar o mapa
  useEffect(() => {
    const propriedadeId = searchParams.get('propriedade');
    const fossaId = searchParams.get('fossa');
    
    if (propriedadeId && propriedades.length > 0) {
      const propriedade = propriedades.find(p => p.id === parseInt(propriedadeId));
      if (propriedade) {
        setMapCenter([propriedade.latitude, propriedade.longitude]);
        setZoom(15);
      }
    } else if (fossaId && fossas.length > 0) {
      const fossa = fossas.find(f => f.id === parseInt(fossaId));
      if (fossa) {
        setMapCenter([fossa.latitude, fossa.longitude]);
        setZoom(18);
      }
    } else if (propriedades.length > 0) {
      // Se houver propriedades na região de Conceição do Castelo, centralizar nelas
      // Caso contrário, manter o centro padrão
      const coordinates = propriedades.map(p => [p.latitude, p.longitude]);
      if (coordinates.length > 0) {
        setMapCenter(calculateCenter(coordinates));
        setZoom(12);
      }
    }
  }, [propriedades, fossas, searchParams]);

  // Aplicar filtros
  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value === '' ? null : value
    }));
  };

  // Limpar filtros
  const limparFiltros = () => {
    setFiltros({
      status: null,
      localidade: null,
      agente: null,
      temFossa: null
    });
  };

  return (
    <div style={{ 
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header compacto - seguindo o padrão dos outros módulos */}
      <div style={{ 
        padding: '8px 15px', 
        backgroundColor: 'white', 
        borderBottom: '1px solid #dee2e6',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        flexShrink: 0,
        zIndex: 1000
      }}>
        <h2 style={{ margin: '0 0 4px 0', fontSize: '18px' }}>Mapa de Propriedades Rurais e Fossas Sépticas</h2>
        <p style={{ color: '#666', margin: 0, fontSize: '12px' }}>
          Clique nos marcadores para informações.
          <span style={{ marginLeft: '8px' }}>
            <span style={{ color: '#28a745', fontWeight: 'bold' }}>■ Mapeada</span>
            <span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '6px' }}>■ Não Mapeada</span>
            <span style={{ color: '#ffc107', fontWeight: 'bold', marginLeft: '6px' }}>■ Em Análise</span>
          </span>
        </p>
        
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '10px', 
          marginTop: '10px',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ marginRight: '5px', fontWeight: '500' }}>Status:</label>
            <select 
              name="status" 
              value={filtros.status || ''}
              onChange={handleFiltroChange}
              style={{ 
                padding: '6px 10px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px', 
                fontSize: '14px' 
              }}
            >
              <option value="">Todos</option>
              <option value="Mapeada">Mapeada</option>
              <option value="Não Mapeada">Não Mapeada</option>
              <option value="Em Análise">Em Análise</option>
            </select>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ marginRight: '5px', fontWeight: '500' }}>Localidade:</label>
            <select 
              name="localidade" 
              value={filtros.localidade || ''}
              onChange={handleFiltroChange}
              style={{ 
                padding: '6px 10px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px', 
                fontSize: '14px' 
              }}
            >
              <option value="">Todas</option>
              {localidades.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.descricao}</option>
              ))}
            </select>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ marginRight: '5px', fontWeight: '500' }}>Agente:</label>
            <select 
              name="agente" 
              value={filtros.agente || ''}
              onChange={handleFiltroChange}
              style={{ 
                padding: '6px 10px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px', 
                fontSize: '14px' 
              }}
            >
              <option value="">Todos</option>
              {agentes.filter(a => a.ativo).map(agente => (
                <option key={agente.id} value={agente.id}>{agente.nome}</option>
              ))}
            </select>
          </div>
          
          <button 
            onClick={limparFiltros}
            style={{ 
              padding: '8px 16px',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              border: '1px solid #ced4da',
              backgroundColor: '#f8f9fa',
              color: '#333'
            }}
          >
            Limpar Filtros
          </button>
        </div>
      </div>
      
      {/* Mapa ocupando todo o espaço restante - seguindo o padrão dos outros módulos */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {loading.propriedades ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%' 
          }}>
            <div style={{ 
              border: '4px solid rgba(0, 0, 0, 0.1)',
              borderLeftColor: '#2e7d32',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ marginTop: '10px' }}>Carregando dados do mapa...</p>
          </div>
        ) : (
          <>
            <MapContainer 
              center={mapCenter} 
              zoom={zoom} 
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              
              <LayersControl position="topright">
                <LayersControl.Overlay checked={true} name="Propriedades">
                  <LayerGroup>
                    {propriedades.map(prop => (
                      <Marker 
                        key={`prop-${prop.id}`}
                        position={[prop.latitude, prop.longitude]} 
                        icon={createCustomIcon(prop.status)}
                      >
                        <Popup>
                          <div className="map-popup">
                            <h3>{prop.nome}</h3>
                            <p><strong>Proprietário:</strong> {prop.nome_proprietario}</p>
                            <p><strong>Localidade:</strong> {prop.localidade?.nome || 'Não definida'}</p>
                            <p><strong>Status:</strong> {prop.status}</p>
                            <p><strong>Coordenadas:</strong> {formatCoordinates(prop.latitude, prop.longitude)}</p>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </LayerGroup>
                </LayersControl.Overlay>
                
                <LayersControl.Overlay checked={showResidencias} name="Residências">
                  <LayerGroup>
                    {residencias.map(res => (
                      <Marker 
                        key={`res-${res.id}`}
                        position={[res.latitude, res.longitude]} 
                        icon={createCustomIcon(res.status, 'residencia')}
                      >
                        <Popup>
                          <div className="map-popup">
                            <h3>{res.identificacao}</h3>
                            <p><strong>Propriedade:</strong> {res.propriedade?.nome || 'Desconhecida'}</p>
                            <p><strong>Moradores:</strong> {res.numero_moradores}</p>
                            <p><strong>Status:</strong> {res.status}</p>
                            <p><strong>Coordenadas:</strong> {formatCoordinates(res.latitude, res.longitude)}</p>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </LayerGroup>
                </LayersControl.Overlay>
                
                <LayersControl.Overlay checked={showFossas} name="Fossas Sépticas">
                  <LayerGroup>
                    {fossas.map(fossa => (
                      <Marker 
                        key={`fossa-${fossa.id}`}
                        position={[fossa.latitude, fossa.longitude]} 
                        icon={createCustomIcon('Mapeada', 'fossa')}
                      >
                        <Popup>
                          <div className="map-popup">
                            <h3>Fossa Séptica</h3>
                            <p><strong>Tipo:</strong> {fossa.tipo}</p>
                            <p><strong>Residência:</strong> {fossa.residencia?.identificacao || 'Desconhecida'}</p>
                            {fossa.data_instalacao && (
                              <p><strong>Instalação:</strong> {new Date(fossa.data_instalacao).toLocaleDateString('pt-BR')}</p>
                            )}
                            {fossa.capacidade && (
                              <p><strong>Capacidade:</strong> {fossa.capacidade}</p>
                            )}
                            <p><strong>Coordenadas:</strong> {formatCoordinates(fossa.latitude, fossa.longitude)}</p>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </LayerGroup>
                </LayersControl.Overlay>
              </LayersControl>
            </MapContainer>
            
            {/* Legenda do mapa - seguindo o padrão dos outros módulos */}
            <div style={{
              position: 'absolute',
              bottom: '20px',
              left: '20px',
              backgroundColor: 'white',
              padding: '12px',
              borderRadius: '6px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              minWidth: '180px'
            }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600' }}>Legenda</h4>
              
              {/* Tipos de marcadores */}
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px', color: '#666' }}>Tipos:</div>
                
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                  <div style={{ 
                    width: '20px', 
                    height: '16px', 
                    marginRight: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    🏠
                  </div>
                  <span style={{ fontSize: '12px' }}>Propriedades</span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                  <div style={{ 
                    width: '20px', 
                    height: '16px', 
                    marginRight: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    🏘️
                  </div>
                  <span style={{ fontSize: '12px' }}>Residências</span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ 
                    width: '20px', 
                    height: '16px', 
                    marginRight: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    ⚫
                  </div>
                  <span style={{ fontSize: '12px' }}>Fossas Sépticas</span>
                </div>
              </div>
              
              {/* Status */}
              <div>
                <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '6px', color: '#666' }}>Status:</div>
                
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ 
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    marginRight: '8px',
                    backgroundColor: '#28a745',
                    border: '1px solid white',
                    boxShadow: '0 0 2px rgba(0, 0, 0, 0.3)'
                  }}></span>
                  <span style={{ fontSize: '12px' }}>Mapeada</span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ 
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    marginRight: '8px',
                    backgroundColor: '#dc3545',
                    border: '1px solid white',
                    boxShadow: '0 0 2px rgba(0, 0, 0, 0.3)'
                  }}></span>
                  <span style={{ fontSize: '12px' }}>Não Mapeada</span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ 
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    marginRight: '8px',
                    backgroundColor: '#ffc107',
                    border: '1px solid white',
                    boxShadow: '0 0 2px rgba(0, 0, 0, 0.3)'
                  }}></span>
                  <span style={{ fontSize: '12px' }}>Em Análise</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MapaPropriedadesRurais;